import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Info, AlertTriangle } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const RATES = [
    { id: "fijo", label: "Fijo", rate: 2.6 },
    { id: "variable", label: "Variable", rate: 3.1 },
    { id: "mixto", label: "Mixto", rate: 2.4 },
];

const IVA_OBRA_NUEVA = 10;
const GASTOS_COMPRAVENTA_PCT = 1; // notaría, registro, gestoría (estimación)

// Tipos generales 2026 por CCAA: ITP (segunda mano) y AJD (obra nueva)
const REGIONS = [
    { id: "andalucia", label: "Andalucía", itp: 7, ajd: 1.2 },
    { id: "aragon", label: "Aragón", itp: 8, ajd: 1.5 },
    { id: "asturias", label: "Asturias", itp: 8, ajd: 1.2 },
    { id: "baleares", label: "Illes Balears", itp: 8, ajd: 1.2 },
    { id: "canarias", label: "Canarias (IGIC)", itp: 6.5, ajd: 0.75 },
    { id: "cantabria", label: "Cantabria", itp: 9, ajd: 1.5 },
    { id: "castilla-la-mancha", label: "Castilla-La Mancha", itp: 9, ajd: 1.5 },
    { id: "castilla-y-leon", label: "Castilla y León", itp: 8, ajd: 1.5 },
    { id: "cataluna", label: "Cataluña", itp: 10, ajd: 1.5 },
    { id: "valenciana", label: "Comunitat Valenciana", itp: 9, ajd: 1.4 },
    { id: "extremadura", label: "Extremadura", itp: 8, ajd: 1.5 },
    { id: "galicia", label: "Galicia", itp: 8, ajd: 1.5 },
    { id: "madrid", label: "Madrid", itp: 6, ajd: 0.75 },
    { id: "murcia", label: "Murcia", itp: 7.75, ajd: 1.5 },
    { id: "navarra", label: "Navarra", itp: 6, ajd: 0.5 },
    { id: "pais-vasco", label: "País Vasco", itp: 4, ajd: 0.5 },
    { id: "la-rioja", label: "La Rioja", itp: 7, ajd: 1 },
];

const fmtEUR = (n) =>
    new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
    }).format(n);

const fmtPct = (n) => n.toFixed(2).replace(".", ",").replace(",00", "") + "%";

function useEditableNumber(value, min, max, onChange) {
    const [text, setText] = useState(String(value));
    const [editing, setEditing] = useState(false);

    useEffect(() => {
        if (!editing) setText(String(value));
    }, [value, editing]);

    const commit = (raw) => {
        const v = Number(raw);
        if (raw !== "" && !isNaN(v)) {
            onChange(Math.min(Math.max(v, min), max));
        } else {
            setText(String(value));
        }
        setEditing(false);
    };

    const inputProps = {
        value: editing ? text : String(value),
        onFocus: () => {
            setEditing(true);
            setText(String(value));
        },
        onChange: (e) => {
            const raw = e.target.value;
            setText(raw);
            const v = Number(raw);
            if (raw !== "" && !isNaN(v) && v >= min && v <= max) onChange(v);
        },
        onBlur: (e) => commit(e.target.value),
        onKeyDown: (e) => {
            if (e.key === "Enter") {
                commit(e.target.value);
                e.target.blur();
            }
        },
    };

    return inputProps;
}

function Slider({ label, testid, min, max, step, value, onChange, suffix }) {
    const fill = ((value - min) / (max - min)) * 100;
    const typed = useEditableNumber(value, min, max, onChange);
    return (
        <div>
            <div className="flex items-center justify-between mb-3 gap-3">
                <label className="text-sm font-medium text-slate-600">
                    {label}
                </label>
                <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-3 py-1 focus-within:border-emerald-500 focus-within:ring-4 focus-within:ring-emerald-500/10 transition-[border-color,box-shadow] duration-200">
                    <input
                        data-testid={`${testid}-input`}
                        type="number"
                        inputMode="numeric"
                        min={min}
                        max={max}
                        step={step}
                        {...typed}
                        className="w-24 sm:w-28 font-mono text-sm font-bold text-[#1F3B5B] text-right bg-transparent outline-none"
                    />
                    <span className="font-mono text-xs text-slate-400">
                        {suffix}
                    </span>
                </div>
            </div>
            <input
                type="range"
                data-testid={testid}
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className="kp-range w-full"
                style={{ "--fill": `${fill}%`, touchAction: "pan-y" }}
            />
        </div>
    );
}

function ResultRow({ label, testid, value, accent }) {
    return (
        <div className="flex justify-between items-center border-b border-white/10 pb-3 gap-4">
            <span className="text-sm text-slate-400">{label}</span>
            <span
                data-testid={testid}
                className={`font-mono font-bold text-right ${
                    accent ? "text-emerald-400" : "text-white"
                }`}
            >
                {value}
            </span>
        </div>
    );
}

export default function Calculator() {
    const [price, setPrice] = useState(250000);
    const [savings, setSavings] = useState(50000);
    const [years, setYears] = useState(25);
    const [type, setType] = useState(RATES[0]);
    const [customRate, setCustomRate] = useState(RATES[0].rate);
    const [region, setRegion] = useState(REGIONS[12]);
    const [isNew, setIsNew] = useState(false);

    const pickRate = (r) => {
        setType(r);
        setCustomRate(r.rate);
    };

    const handleCustomRate = (v) => {
        setCustomRate(v);
        const match = RATES.find((r) => r.rate === v);
        if (match) setType(match);
    };

    const tinTyped = useEditableNumber(customRate, 0, 15, handleCustomRate);

    const { loan, monthly, pct, taxBase, itp, iva, ajd, taxes, gastos, entrada, totalContribution } =
        useMemo(() => {
            const r = customRate / 1200;
            const n = years * 12;
            const taxBase = price;
            const itp = isNew ? 0 : (taxBase * region.itp) / 100;
            const iva = isNew ? (taxBase * IVA_OBRA_NUEVA) / 100 : 0;
            const ajd = isNew ? (taxBase * region.ajd) / 100 : 0;
            const taxes = itp + iva + ajd;
            const gastos = (price * GASTOS_COMPRAVENTA_PCT) / 100;
            // Los ahorros aportados cubren: entrada + impuestos + gastos
            const entrada = Math.max(savings - taxes - gastos, 0);
            const loan = Math.max(price - entrada, 0);
            const monthly =
                loan > 0 ? (loan * r) / (1 - Math.pow(1 + r, -n)) : 0;
            const pct = price > 0 ? (loan / price) * 100 : 0;
            const totalContribution = entrada + taxes + gastos;
            return {
                loan,
                monthly,
                pct,
                taxBase,
                itp,
                iva,
                ajd,
                taxes,
                gastos,
                entrada,
                totalContribution,
            };
        }, [price, savings, years, customRate, region, isNew]);

    const requestQuote = () => {
        const simulation = [
            `Vivienda: ${fmtEUR(price)} (${
                isNew ? "obra nueva" : "segunda mano"
            }, ${region.label})`,
            `Cuota estimada: ${fmtEUR(monthly)}/mes · TIN ${fmtPct(
                customRate
            )} · ${years} años`,
            `Entrada: ${fmtEUR(entrada)} · ${
                isNew
                    ? `IVA ${fmtEUR(iva)} + AJD ${fmtEUR(ajd)}`
                    : `ITP ${fmtEUR(itp)}`
            } · Gastos: ${fmtEUR(gastos)}`,
            `Aportación total: ${fmtEUR(totalContribution)} · Préstamo: ${fmtEUR(
                loan
            )} (${pct.toFixed(0)}%)`,
        ].join("\n");
        window.dispatchEvent(
            new CustomEvent("kp:prefill", {
                detail: {
                    amount: String(loan),
                    mortgage_type:
                        customRate === type.rate
                            ? `Tipo ${type.label}`
                            : `TIN personalizado ${fmtPct(customRate)}`,
                    simulation,
                },
            })
        );
        scrollToId("contacto");
    };

    return (
        <section
            id="calculadora"
            data-testid="calculator-section"
            className="bg-[#F5F7FA] py-20 lg:py-28 px-4 sm:px-6 lg:px-8"
        >
            <div className="max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.6 }}
                    className="max-w-2xl"
                >
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-emerald-600">
                        Simulador
                    </p>
                    <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A]">
                        Calcula tu cuota en segundos
                    </h2>
                    <p className="mt-4 text-base sm:text-lg text-slate-600">
                        Ajusta los parámetros y obtén una estimación instantánea
                        de tu cuota mensual, impuestos y aportación total.
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="mt-12 grid lg:grid-cols-5 rounded-[2rem] overflow-hidden border border-slate-200 shadow-2xl shadow-slate-300/40"
                >
                    <div className="lg:col-span-3 bg-white p-7 sm:p-10 flex flex-col gap-8">
                        <Slider
                            label="Precio de la vivienda"
                            testid="calc-price-slider"
                            min={80000}
                            max={800000}
                            step={5000}
                            value={price}
                            onChange={setPrice}
                            suffix="€"
                        />
                        <Slider
                            label="Ahorros aportados (entrada + impuestos + gastos)"
                            testid="calc-savings-slider"
                            min={0}
                            max={Math.max(price - 20000, 40000)}
                            step={1000}
                            value={Math.min(savings, price - 20000)}
                            onChange={setSavings}
                            suffix="€"
                        />
                        <Slider
                            label="Plazo de amortización"
                            testid="calc-years-slider"
                            min={10}
                            max={40}
                            step={1}
                            value={years}
                            onChange={setYears}
                            suffix="años"
                        />

                        <div>
                            <p className="text-sm font-medium text-slate-600 mb-3">
                                Tipo de vivienda
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                                {[
                                    { id: false, label: "Segunda mano", sub: "ITP" },
                                    { id: true, label: "Obra nueva", sub: "IVA + AJD" },
                                ].map((opt) => (
                                    <button
                                        key={opt.label}
                                        data-testid={
                                            opt.id
                                                ? "calc-vivienda-nueva"
                                                : "calc-vivienda-usada"
                                        }
                                        onClick={() => setIsNew(opt.id)}
                                        className={`rounded-2xl border px-4 py-3 text-center transition-[background-color,border-color,transform] duration-200 hover:-translate-y-0.5 ${
                                            isNew === opt.id
                                                ? "bg-[#1F3B5B] border-[#1F3B5B] text-white"
                                                : "bg-white border-slate-200 text-slate-700 hover:border-[#1F3B5B]/40"
                                        }`}
                                    >
                                        <span className="block font-semibold text-sm">
                                            {opt.label}
                                        </span>
                                        <span
                                            className={`block font-mono text-xs mt-0.5 ${
                                                isNew === opt.id
                                                    ? "text-emerald-300"
                                                    : "text-slate-400"
                                            }`}
                                        >
                                            {opt.sub}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-600 mb-3">
                                Comunidad autónoma
                            </label>
                            <select
                                data-testid="calc-region-select"
                                value={region.id}
                                onChange={(e) =>
                                    setRegion(
                                        REGIONS.find(
                                            (r) => r.id === e.target.value
                                        )
                                    )
                                }
                                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[15px] text-slate-800 outline-none transition-[border-color,box-shadow] duration-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
                            >
                                {REGIONS.map((r) => (
                                    <option key={r.id} value={r.id}>
                                        {r.label} ·{" "}
                                        {isNew
                                            ? `AJD ${fmtPct(r.ajd)}`
                                            : `ITP ${fmtPct(r.itp)}`}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <p className="text-sm font-medium text-slate-600 mb-3">
                                Tipo de interés
                            </p>
                            <div className="flex gap-3">
                                {RATES.map((r) => (
                                    <button
                                        key={r.id}
                                        data-testid={`calc-rate-${r.id}`}
                                        onClick={() => pickRate(r)}
                                        className={`flex-1 rounded-2xl border px-4 py-3 text-center transition-[background-color,border-color,transform] duration-200 hover:-translate-y-0.5 ${
                                            customRate === r.rate
                                                ? "bg-[#1F3B5B] border-[#1F3B5B] text-white"
                                                : "bg-white border-slate-200 text-slate-700 hover:border-[#1F3B5B]/40"
                                        }`}
                                    >
                                        <span className="block font-semibold text-sm">
                                            {r.label}
                                        </span>
                                        <span
                                            className={`block font-mono text-xs mt-0.5 ${
                                                customRate === r.rate
                                                    ? "text-emerald-300"
                                                    : "text-slate-400"
                                            }`}
                                        >
                                            {fmtPct(r.rate)}
                                        </span>
                                    </button>
                                ))}
                            </div>
                            <div className="mt-4 flex items-center gap-3">
                                <label className="text-sm font-medium text-slate-600">
                                    O escribe tu TIN:
                                </label>
                                <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-3 py-1.5 focus-within:border-emerald-500 focus-within:ring-4 focus-within:ring-emerald-500/10 transition-[border-color,box-shadow] duration-200">
                                    <input
                                        data-testid="calc-custom-rate-input"
                                        type="number"
                                        inputMode="decimal"
                                        min={0}
                                        max={15}
                                        step={0.05}
                                        {...tinTyped}
                                        className="w-20 font-mono text-sm font-bold text-[#1F3B5B] text-right bg-transparent outline-none"
                                    />
                                    <span className="font-mono text-xs text-slate-400">
                                        %
                                    </span>
                                </div>
                            </div>
                            <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-400">
                                <Info size={13} /> Tipos de interés e impuestos
                                orientativos (tipo general). Pueden existir
                                tipos reducidos según tu perfil. Tu asesor
                                negociará las condiciones finales.
                            </p>
                        </div>
                    </div>

                    <div className="lg:col-span-2 bg-[#112338] noise-overlay relative p-7 sm:p-10 flex flex-col justify-between">
                        <div className="absolute inset-0 blueprint-grid opacity-60" />
                        <div className="relative">
                            <p className="font-mono text-xs uppercase tracking-[0.25em] text-emerald-400">
                                Tu cuota estimada
                            </p>
                            <div
                                data-testid="calc-monthly-result"
                                className="mt-4 font-display font-extrabold text-white text-5xl sm:text-6xl tracking-tight"
                            >
                                <AnimatePresence mode="popLayout">
                                    <motion.span
                                        key={Math.round(monthly)}
                                        initial={{ opacity: 0, y: 14 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -14 }}
                                        transition={{ duration: 0.25 }}
                                        className="inline-block"
                                    >
                                        {fmtEUR(monthly)}
                                    </motion.span>
                                </AnimatePresence>
                                <span className="text-lg font-semibold text-slate-400">
                                    {" "}
                                    /mes
                                </span>
                            </div>

                            <div className="mt-8 space-y-4">
                                <ResultRow
                                    label="Préstamo necesario"
                                    testid="calc-loan-result"
                                    value={fmtEUR(loan)}
                                />
                                <ResultRow
                                    label="Financiación"
                                    testid="calc-pct-result"
                                    value={`${pct.toFixed(0)}%`}
                                    accent
                                />
                                <ResultRow
                                    label="Entrada (parte no financiada)"
                                    testid="calc-deposit-result"
                                    value={fmtEUR(entrada)}
                                />
                                {isNew ? (
                                    <>
                                        <ResultRow
                                            label={`IVA (${IVA_OBRA_NUEVA}% s/ ${fmtEUR(taxBase)})`}
                                            testid="calc-iva-result"
                                            value={fmtEUR(iva)}
                                        />
                                        <ResultRow
                                            label={`AJD ${region.label} (${fmtPct(region.ajd)})`}
                                            testid="calc-ajd-result"
                                            value={fmtEUR(ajd)}
                                        />
                                    </>
                                ) : (
                                    <ResultRow
                                        label={`ITP ${region.label} (${fmtPct(region.itp)} s/ ${fmtEUR(taxBase)})`}
                                        testid="calc-itp-result"
                                        value={fmtEUR(itp)}
                                    />
                                )}
                                <ResultRow
                                    label={`Gastos de compraventa (~${fmtPct(GASTOS_COMPRAVENTA_PCT)})`}
                                    testid="calc-expenses-result"
                                    value={fmtEUR(gastos)}
                                />
                                <ResultRow
                                    label="Aportación total (entrada + impuestos + gastos)"
                                    testid="calc-contribution-result"
                                    value={fmtEUR(totalContribution)}
                                    accent
                                />
                            </div>

                            {taxes + gastos > savings && (
                                <div
                                    data-testid="calc-savings-warning"
                                    className="mt-6 rounded-2xl border border-amber-400/40 bg-amber-400/10 p-4 flex gap-3"
                                >
                                    <AlertTriangle
                                        size={18}
                                        className="text-amber-400 shrink-0 mt-0.5"
                                    />
                                    <p className="text-[13px] text-amber-200/95 leading-snug">
                                        Con estos ahorros no cubres los
                                        impuestos y gastos de la compra: te
                                        faltarían{" "}
                                        <strong>
                                            {fmtEUR(taxes + gastos - savings)}
                                        </strong>
                                        . La entrada quedaría en 0 € y
                                        necesitarías financiación del 100%.
                                    </p>
                                </div>
                            )}
                        </div>

                        <button
                            data-testid="calc-request-cta"
                            onClick={requestQuote}
                            className="relative mt-10 group inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0B1A2B] font-bold px-6 py-4 transition-[background-color,transform] duration-200 hover:-translate-y-0.5 w-full"
                        >
                            Solicitar esta cuota
                            <ArrowRight
                                size={17}
                                className="transition-transform duration-200 group-hover:translate-x-1"
                            />
                        </button>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
